import apiClient from './apiClient'
import { ROLE_PERMISSIONS } from '@features/roles/constants/roleConstants'

/**
 * Authentication service — API (backend) implementation.
 *
 * The backend has no JWT. Login returns { message, user }.
 * We store the normalized user in localStorage so getProfile()
 * can restore the session on page refresh without a /me endpoint.
 *
 * "Access token" = user._id, used only as a session presence marker
 * by the existing auth store / AuthProvider flow.
 */

const SESSION_USER_KEY = 'aftersubmit_session_user'

// ── Helpers ──────────────────────────────────────────────────────────────────

function normalizeUser(backendUser) {
  const role = backendUser.role ?? (backendUser.isAdmin ? 'admin' : 'user')
  return {
    ...backendUser,
    id:          backendUser._id?.toString() ?? backendUser._id,
    role,
    companyId:   backendUser.companyId?._id?.toString?.() ?? backendUser.companyId ?? null,
    companyName: backendUser.companyName ?? backendUser.companyId?.companyName ?? '',
    status:      backendUser.status ?? 'active',
    permissions: ROLE_PERMISSIONS[role] ?? [],
    social:      backendUser.social ?? {
      website:  '',
      linkedin: '',
      twitter:  '',
      github:   '',
    },
  }
}

function makeError(message, status = 400) {
  return Object.assign(new Error(message), { status })
}

// ── Service ───────────────────────────────────────────────────────────────────

const authApiService = {
  /**
   * POST /auth/login — validate credentials, normalize user, persist session.
   * Returns { user, accessToken, refreshToken } so the auth store can hydrate.
   */
  async login({ email, password }) {
    const result = await apiClient.post('/auth/login', { email, password })
    const user   = normalizeUser(result.user)

    // Persist full user for getProfile() on page refresh
    localStorage.setItem(SESSION_USER_KEY, JSON.stringify(user))

    // Use the real JWT token from backend
    const accessToken = result.token || user.id

    return { user, accessToken, refreshToken: '' }
  },

  /**
   * GET /auth/me — fetch the current user from the backend on session restore.
   */
  async getProfile() {
    const token = localStorage.getItem('agrocast_access_token')
    if (!token) throw makeError('Not authenticated', 401)

    const result = await apiClient.get('/auth/me', { params: { userId: token } })
    const user   = normalizeUser(result.user)

    localStorage.setItem(SESSION_USER_KEY, JSON.stringify(user))
    return user
  },

  /** Clear the session from localStorage. */
  async logout() {
    localStorage.removeItem(SESSION_USER_KEY)
    return { success: true }
  },

  /**
   * POST /auth/register — create a new user account.
   * Used by admin when creating users (via userService which delegates here).
   */
  async register({ name, email, phone, password, isAdmin = false }) {
    const result = await apiClient.post('/auth/register', { name, email, phone, password, isAdmin })
    return normalizeUser(result.user)
  },

  /**
   * Update the current user's profile via PUT /users/:id.
   * Refreshes the persisted session user.
   */
  async updateProfile(data) {
    const token = localStorage.getItem('agrocast_access_token')
    if (!token) throw makeError('Not authenticated', 401)

    const result = await apiClient.put(`/users/${token}`, data)
    const user   = normalizeUser(result.user)

    localStorage.setItem(SESSION_USER_KEY, JSON.stringify(user))
    return user
  },

  // ── Graceful stubs for missing endpoints ──────────────────────────────────

  async forgotPassword() {
    return { message: 'Password reset is handled by the administrator.' }
  },

  async resetPassword() {
    throw makeError('Password reset is handled by the administrator.', 403)
  },
}

export { normalizeUser }
export default authApiService
